/**
 * Diagnostics card for the Bank Balance chart's snapshot job (dashboard
 * redesign step 5). Shows last-snapshot time + doc count, a "Run Snapshot Now"
 * button for the daily job, and a Preview/Apply flow for the transaction-based
 * backfill.
 *
 * The backfill is meant to be RE-RUN, not called once and forgotten: prod's
 * transaction history is thin until a later historical-data backfill lands,
 * so the reconstructed months will be flat until then. Preview never writes
 * (dry_run=true); Apply requires confirming after reviewing the preview.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Play, Eye, Check, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { cashJurisdictionSnapshotAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function relativeTime(isoDate) {
  if (!isoDate) return 'Never';
  const diff = Date.now() - new Date(isoDate).getTime();
  const hours = diff / 3600000;
  if (hours < 1) return Math.round(hours * 60) + 'm ago';
  if (hours < 24) return Math.round(hours) + 'h ago';
  return Math.round(hours / 24) + 'd ago';
}

export function CashJurisdictionSnapshotPanel() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [runningNow, setRunningNow] = useState(false);
  // Default reaches January 1 of the current calendar year regardless of
  // today's month (dashboards round 3's Jan-Dec chart axis): new Date()
  // .getMonth() is 0-indexed and already equals "months since January".
  const [monthsBack, setMonthsBack] = useState(Math.max(1, new Date().getMonth()));
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [cleanupPreviewing, setCleanupPreviewing] = useState(false);
  const [cleanupApplying, setCleanupApplying] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [coverageMonths, setCoverageMonths] = useState(6);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageDetail, setCoverageDetail] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await cashJurisdictionSnapshotAPI.diagnostics();
      setStatus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const runNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await cashJurisdictionSnapshotAPI.runNow();
      toast.success('Snapshot taken for the current month');
      // The snapshot itself always succeeds independently of the trailing-
      // history self-heal below it -- a self-heal failure (FX provider down,
      // a transient Mongo timeout) must still be shown, not buried in a log
      // the operator can't read (CLAUDE.md: operator has no shell access).
      if (data?.backfill_error) {
        toast.warning(`Snapshot saved, but auto-backfill of trailing history failed: ${data.backfill_error}`);
      }
      await fetchStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRunningNow(false);
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await cashJurisdictionSnapshotAPI.backfillPreview(monthsBack);
      setPreview(data);
      if (data.written.length === 0) {
        toast.success('Preview: nothing to backfill (every month already has an exact snapshot)');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    if (!preview || preview.written.length === 0) return;
    if (!window.confirm(
      `Apply backfill for ${preview.written.length} month/jurisdiction row(s)? `
      + 'This overwrites any existing reconstructed figures for those months.'
    )) return;
    setApplying(true);
    try {
      const { data } = await cashJurisdictionSnapshotAPI.backfillApply(monthsBack);
      toast.success(`Backfill applied: ${data.written.length} row(s) written`);
      setPreview(null);
      await fetchStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const runCleanupPreview = async () => {
    setCleanupPreviewing(true);
    setCleanupPreview(null);
    try {
      const { data } = await cashJurisdictionSnapshotAPI.cleanupPreview();
      setCleanupPreview(data);
      if (data.found.length === 0) {
        toast.success('Preview: no fabricated history found -- nothing to clean up');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCleanupPreviewing(false);
    }
  };

  const runCleanupApply = async () => {
    if (!cleanupPreview || cleanupPreview.found.length === 0) return;
    if (!window.confirm(
      `Delete ${cleanupPreview.found.length} fabricated month/jurisdiction row(s)? `
      + 'These were reconstructed before the jurisdiction\'s actual transaction coverage began, '
      + 'so the true balance cannot be recovered -- this restores the honest gap instead.'
    )) return;
    setCleanupApplying(true);
    try {
      const { data } = await cashJurisdictionSnapshotAPI.cleanupApply();
      toast.success(`Cleanup applied: ${data.deleted_count} row(s) deleted`);
      setCleanupPreview(null);
      await fetchStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCleanupApplying(false);
    }
  };

  const runCoverageDetail = async () => {
    setCoverageLoading(true);
    try {
      const { data } = await cashJurisdictionSnapshotAPI.coverageDetail(coverageMonths);
      setCoverageDetail(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCoverageLoading(false);
    }
  };

  const bySource = status?.by_source || {};

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base">Cash Jurisdiction Snapshot</CardTitle>
            <CardDescription>
              Bank Balance chart's monthly balance-by-jurisdiction history (UK / France / Personal)
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runNow}
              disabled={runningNow}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
              data-testid="diag-cash-jurisdiction-run-now-btn"
            >
              {runningNow ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              Run Snapshot Now
            </button>
            <button
              onClick={fetchStatus}
              disabled={loading}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Load'}
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {status && (
          <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground flex-wrap">
            <span>Last updated: {relativeTime(status.last_updated_at)}</span>
            <span>Months covered: {status.earliest_month || '-'} to {status.latest_month || '-'}</span>
            <Badge variant="outline" className="text-[10px]">{status.total_docs} docs</Badge>
            <Badge className="bg-green-100 text-green-700 text-[10px]">{bySource.snapshot || 0} exact</Badge>
            <Badge className="bg-amber-100 text-amber-700 text-[10px]">{bySource.reconstructed || 0} reconstructed</Badge>
          </div>
        )}

        <div className="border-t pt-4">
          <p className="text-xs text-muted-foreground mb-2">
            Reconstruct past months from transaction history. Never overwrites an exact
            snapshot. Re-run this after transaction history changes materially (e.g. once a
            historical-data backfill lands) -- reconstructed months are only as good as the
            transaction history available when this runs.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs text-muted-foreground" htmlFor="cjs-months-back">Months back</label>
            <Input
              id="cjs-months-back"
              type="number"
              min={1}
              max={36}
              value={monthsBack}
              onChange={(e) => setMonthsBack(Math.max(1, Math.min(36, parseInt(e.target.value, 10) || 1)))}
              className="h-8 w-20 text-xs"
            />
            <button
              onClick={runPreview}
              disabled={previewing}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
              data-testid="diag-cash-jurisdiction-preview-btn"
            >
              {previewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
              Preview
            </button>
            <button
              onClick={runApply}
              disabled={applying || !preview || preview.written.length === 0}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:bg-background disabled:text-foreground"
              data-testid="diag-cash-jurisdiction-apply-btn"
            >
              {applying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Apply
            </button>
          </div>

          {preview && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="text-[10px]">PREVIEW (read-only)</Badge>
                <span className="text-xs text-muted-foreground">
                  {preview.written.length} row(s) would change, {preview.skipped_existing_snapshot.length} skipped (exact snapshot present)
                </span>
              </div>
              {preview.written.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead>Jurisdiction</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">Would become</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.written.map((row, i) => (
                      <TableRow key={row.month + '-' + row.jurisdiction + '-' + i}>
                        <TableCell className="text-xs">{row.month}</TableCell>
                        <TableCell className="text-xs">{row.jurisdiction}</TableCell>
                        <TableCell className="text-right text-xs font-mono">
                          {row.previous_balance_gbp != null ? `£${row.previous_balance_gbp.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-right text-xs font-mono">
                          £{row.new_balance_gbp.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </div>

        <div className="border-t pt-4 mt-4">
          <p className="text-xs text-muted-foreground mb-2">
            Find and remove fabricated history: months reconstructed before a jurisdiction's actual
            transaction coverage began (e.g. by an earlier run, before the coverage guard shipped).
            There is no way to recover the true balance for a fabricated month, so this deletes it
            rather than correcting it -- restoring the honest gap the chart promises for missing
            history, instead of leaving a wrong number in place. Safe to re-run: an empty result
            means nothing needs cleaning up.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={runCleanupPreview}
              disabled={cleanupPreviewing}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
              data-testid="diag-cash-jurisdiction-cleanup-preview-btn"
            >
              {cleanupPreviewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
              Preview cleanup
            </button>
            <button
              onClick={runCleanupApply}
              disabled={cleanupApplying || !cleanupPreview || cleanupPreview.found.length === 0}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:bg-background disabled:text-foreground disabled:border-input"
              data-testid="diag-cash-jurisdiction-cleanup-apply-btn"
            >
              {cleanupApplying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              Delete fabricated history
            </button>
          </div>

          {cleanupPreview && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="text-[10px]">PREVIEW (read-only)</Badge>
                <span className="text-xs text-muted-foreground">
                  {cleanupPreview.found.length} fabricated row(s) found
                </span>
              </div>
              {cleanupPreview.found.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead>Jurisdiction</TableHead>
                      <TableHead className="text-right">Fabricated balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cleanupPreview.found.map((row, i) => (
                      <TableRow key={row.month + '-' + row.jurisdiction + '-' + i}>
                        <TableCell className="text-xs">{row.month}</TableCell>
                        <TableCell className="text-xs">{row.jurisdiction}</TableCell>
                        <TableCell className="text-right text-xs font-mono">
                          {row.balance_gbp != null ? `£${row.balance_gbp.toLocaleString()}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </div>

        <div className="border-t pt-4 mt-4">
          <p className="text-xs text-muted-foreground mb-2">
            Coverage detail: per jurisdiction per month, which accounts are contributing
            (covered) vs excluded as not-yet-covered -- the same account-level universe the
            backfill guard and cleanup use, so a partial-coverage month is visible here
            instead of only inferred from the chart's shape. Also lists accounts excluded
            from the predicate entirely because they are hidden/archived.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs text-muted-foreground" htmlFor="cjs-coverage-months">Months</label>
            <Input
              id="cjs-coverage-months"
              type="number"
              min={1}
              max={36}
              value={coverageMonths}
              onChange={(e) => setCoverageMonths(Math.max(1, Math.min(36, parseInt(e.target.value, 10) || 1)))}
              className="h-8 w-20 text-xs"
            />
            <button
              onClick={runCoverageDetail}
              disabled={coverageLoading}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
              data-testid="diag-cash-jurisdiction-coverage-detail-btn"
            >
              {coverageLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
              Show coverage
            </button>
          </div>

          {coverageDetail && Object.keys(coverageDetail).length === 0 && (
            <p className="text-xs text-muted-foreground">No jurisdiction contributes cash right now.</p>
          )}

          {coverageDetail && Object.entries(coverageDetail).map(([jurisdiction, jd]) => (
            <div key={jurisdiction} className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-[10px]">{jurisdiction}</Badge>
                {jd.hidden_accounts_excluded.length > 0 && (
                  <span className="text-[11px] text-amber-700">
                    {jd.hidden_accounts_excluded.length} hidden account(s) excluded from this jurisdiction entirely: {' '}
                    {jd.hidden_accounts_excluded.map((h) => h.label).join(', ')}
                  </span>
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Contributing accounts</TableHead>
                    <TableHead>Excluded (not yet covered)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jd.months.map((m) => (
                    <TableRow key={jurisdiction + '-' + m.month}>
                      <TableCell className="text-xs">{m.month}</TableCell>
                      <TableCell className="text-xs">
                        {m.contributing.length === 0 ? '-' : m.contributing.map((a) => a.label).join(', ')}
                      </TableCell>
                      <TableCell className="text-xs text-amber-700">
                        {m.excluded_uncovered.length === 0 ? '-' : m.excluded_uncovered.map((a) => a.label).join(', ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
